import {useEffect, useMemo, useState} from 'react'
import {getPromo, saveTemplate, sendTest, sendPromo, PromoRecipient, PromoSeen} from './_requests'

const BASE = typeof window !== 'undefined' ? window.location.origin : ''

function renderPreview(tmpl: string): string {
  return (tmpl || '')
    .replace(/\{name\}/g, 'ישראל')
    .replace(/\{link\}/g, `${BASE}/auth/register?aff=ABC123`)
}
function fmt(d?: string | null) {
  if (!d) return '—'
  const x = new Date(d)
  return isNaN(x.getTime()) ? '—' : x.toLocaleString('he-IL', {day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'})
}

export default function AdminReferralPromoPage() {
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [smsConfigured, setSmsConfigured] = useState(true)
  const [template, setTemplate] = useState('')
  const [savedTemplate, setSavedTemplate] = useState('')
  const [recipients, setRecipients] = useState<PromoRecipient[]>([])
  const [seen, setSeen] = useState<PromoSeen[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const [savingTpl, setSavingTpl] = useState(false)
  const [tplMsg, setTplMsg] = useState('')
  const [testPhone, setTestPhone] = useState('')
  const [testMsg, setTestMsg] = useState('')
  const [testing, setTesting] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendMsg, setSendMsg] = useState('')
  const [tab, setTab] = useState<'sms' | 'seen'>('sms')

  const load = async () => {
    setLoading(true)
    try {
      const {data} = await getPromo()
      setTemplate(data.template || '')
      setSavedTemplate(data.template || '')
      setRecipients(data.recipients || [])
      setSeen(data.seen || [])
      setSmsConfigured(data.sms_configured !== false)
      setSelected(new Set((data.recipients || []).map((r) => r.id)))
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'שגיאה בטעינה')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
  }, [])

  const withPhone = useMemo(() => recipients.filter((r) => (r.phone || '').trim()), [recipients])
  const allSelected = selected.size > 0 && selected.size === recipients.length
  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(recipients.map((r) => r.id)))

  const doSaveTemplate = async () => {
    setSavingTpl(true)
    setTplMsg('')
    try {
      await saveTemplate(template)
      setSavedTemplate(template)
      setTplMsg('נשמר ✓')
    } catch (e: any) {
      setTplMsg(e?.response?.data?.error || 'שגיאה')
    } finally {
      setSavingTpl(false)
    }
  }
  const doTest = async () => {
    if (!testPhone.trim()) return
    setTesting(true)
    setTestMsg('')
    try {
      const {data} = await sendTest({phone: testPhone.trim(), template, base_url: BASE})
      setTestMsg(data.ok ? 'נשלח ✓' : data.error || 'שגיאה')
    } catch (e: any) {
      setTestMsg(e?.response?.data?.error || 'שגיאה בשליחה')
    } finally {
      setTesting(false)
    }
  }
  const doSend = async (all: boolean) => {
    const ids = Array.from(selected)
    const count = all ? recipients.length : ids.length
    if (!count) {
      setSendMsg('לא נבחרו נמענים')
      return
    }
    if (!window.confirm(`לשלוח SMS ל-${count} לקוחות?`)) return
    setSending(true)
    setSendMsg('שולח…')
    try {
      const {data} = await sendPromo(all ? {all: true, template, base_url: BASE} : {account_ids: ids, template, base_url: BASE})
      setSendMsg(`נשלחו ${data.sent}, נכשלו ${data.failed}, דולגו ${data.skipped} (מתוך ${data.total})`)
      await load()
    } catch (e: any) {
      setSendMsg(e?.response?.data?.error || 'שגיאה בשליחה')
    } finally {
      setSending(false)
    }
  }

  if (loading) return <div className='p-10 text-muted' dir='rtl'>טוען…</div>

  return (
    <div className='p-6' dir='rtl' style={{maxWidth: 1000, margin: '0 auto'}}>
      <h2 className='mb-4'>תוכנית הפניות — פופאפ ו-SMS</h2>
      {err && <div className='alert alert-danger'>{err}</div>}
      {!smsConfigured && <div className='alert alert-warning'>שירות ה-SMS אינו מוגדר — ניתן לערוך את ההודעה אך לא לשלוח.</div>}

      <div className='mb-4 fs-8 text-muted'>
        פופאפ חודשי מוצג ללקוחות ששילמו לפחות פעם אחת והמנוי שלהם פעיל. לתצוגה מקדימה של הפופאפ פתחו את המערכת עם{' '}
        <code dir='ltr'>?preview_referral_promo=1</code>.
      </div>

      <ul className='nav nav-tabs nav-line-tabs mb-4 gap-8'>
        <li className='nav-item'>
          <a className={`nav-link ${tab === 'sms' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setTab('sms')}>
            שליחת SMS
          </a>
        </li>
        <li className='nav-item'>
          <a className={`nav-link ${tab === 'seen' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setTab('seen')}>
            ראו את הפופאפ ({seen.length})
          </a>
        </li>
      </ul>

      {tab === 'sms' && (
        <>
          {/* Template */}
          <div className='card mb-5'>
            <div className='card-body'>
              <label className='form-label fs-7 fw-bold'>תבנית ההודעה</label>
              <div className='fs-8 text-muted mb-2'>
                ניתן להשתמש ב־<code dir='ltr'>{'{name}'}</code> (שם הלקוח) וב־<code dir='ltr'>{'{link}'}</code> (הלינק האישי של הלקוח).
              </div>
              <textarea className='form-control form-control-sm' rows={3} dir='rtl' value={template} onChange={(e) => setTemplate(e.target.value)} />
              <div className='mt-2 p-2 rounded' style={{background: 'rgba(80,131,230,0.08)', border: '1px solid rgba(80,131,230,0.25)', whiteSpace: 'pre-wrap'}}>
                <div className='fs-8 text-muted mb-1'>תצוגה מקדימה</div>
                <div className='fs-7' dir='rtl'>{renderPreview(template)}</div>
              </div>
              <div className='d-flex gap-2 align-items-center mt-2 flex-wrap'>
                <button className='btn btn-sm btn-primary' onClick={doSaveTemplate} disabled={savingTpl || template === savedTemplate}>
                  {savingTpl ? 'שומר…' : 'שמור תבנית'}
                </button>
                {tplMsg && <span className='text-muted fs-8'>{tplMsg}</span>}
              </div>
            </div>
          </div>

          {/* Test */}
          <div className='card mb-5'>
            <div className='card-body d-flex gap-2 align-items-center flex-wrap'>
              <span className='fs-7 fw-bold'>שליחת בדיקה:</span>
              <input className='form-control form-control-sm' style={{width: 180}} dir='ltr' placeholder='0501234567' value={testPhone} onChange={(e) => setTestPhone(e.target.value)} />
              <button className='btn btn-sm btn-light-primary' onClick={doTest} disabled={testing || !smsConfigured || !testPhone.trim()}>
                {testing ? 'שולח…' : 'שלח בדיקה'}
              </button>
              {testMsg && <span className='text-muted fs-8'>{testMsg}</span>}
            </div>
          </div>

          {/* Recipients */}
          <div className='card'>
            <div className='card-header align-items-center'>
              <h3 className='card-title mb-0'>נמענים ({recipients.length}) · עם טלפון: {withPhone.length}</h3>
              <div className='d-flex gap-2 align-items-center'>
                {sendMsg && <span className='text-muted fs-8'>{sendMsg}</span>}
                <button className='btn btn-sm btn-light' onClick={() => doSend(false)} disabled={sending || !smsConfigured || selected.size === 0}>
                  שלח לנבחרים ({selected.size})
                </button>
                <button className='btn btn-sm btn-success' onClick={() => doSend(true)} disabled={sending || !smsConfigured || recipients.length === 0}>
                  שלח לכולם
                </button>
              </div>
            </div>
            <div className='card-body p-0'>
              <div className='table-responsive'>
                <table className='table table-row-bordered align-middle mb-0'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='ps-4' style={{width: 40}}>
                        <input type='checkbox' checked={allSelected} onChange={toggleAll} style={{width: 16, height: 16}} />
                      </th>
                      <th>שם</th>
                      <th>טלפון</th>
                      <th>נשלח לאחרונה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.length === 0 ? (
                      <tr><td colSpan={4} className='text-center text-muted py-6'>אין נמענים כשירים.</td></tr>
                    ) : (
                      recipients.map((r) => (
                        <tr key={r.id}>
                          <td className='ps-4'>
                            <input type='checkbox' checked={selected.has(r.id)} onChange={() => toggle(r.id)} style={{width: 16, height: 16}} />
                          </td>
                          <td className='fw-bold'>{r.name}</td>
                          <td dir='ltr' className='text-end'>{r.phone || '—'}</td>
                          <td className='text-muted fs-8'>{fmt(r.sms_sent_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'seen' && (
        <div className='card'>
          <div className='card-header align-items-center'>
            <h3 className='card-title mb-0'>לקוחות שראו את הפופאפ ({seen.length})</h3>
          </div>
          <div className='card-body p-0'>
            <div className='table-responsive'>
              <table className='table table-row-bordered align-middle mb-0'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-4'>שם</th>
                    <th>אימייל</th>
                    <th>טלפון</th>
                    <th>נצפה בתאריך</th>
                  </tr>
                </thead>
                <tbody>
                  {seen.length === 0 ? (
                    <tr><td colSpan={4} className='text-center text-muted py-6'>עדיין אף אחד לא ראה את הפופאפ.</td></tr>
                  ) : (
                    seen.map((s) => (
                      <tr key={s.id}>
                        <td className='ps-4 fw-bold'>{s.name}</td>
                        <td className='text-muted fs-8'>{s.email || '—'}</td>
                        <td dir='ltr' className='text-end'>{s.phone || '—'}</td>
                        <td className='text-muted fs-8'>{fmt(s.seen_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
